import { PlusOutlined } from '@ant-design/icons';
import { Eye, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button, TableColumnsType, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { Product, getProducts } from '@hooks';
import { ProductFormModal } from './ProductFormModal';
import { DeleteProductModal } from './DeleteProductModal';

const { Paragraph } = Typography;

enum OrderEnum {
  ascend = 'asc',
  descend = 'desc',
}

export const ProductsManagementPage = () => {
  const navigate = useNavigate();

  // States to control modals
  const [productFormModalOpen, setProductFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // States for pagination and search
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string | undefined>('descend');

  // Parameters for product search
  const params = {
    page,
    limit,
    search,
    orderBy: sortField,
    orderType: sortOrder ? OrderEnum[sortOrder as keyof typeof OrderEnum] : undefined,
  };

  // Fetch products using the hook
  const { data, isLoading, refetch } = getProducts(params);

  // Refetch when params change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, limit, search, sortOrder, sortField, refetch]);

  // Check if data exists before accessing properties
  const products = data?.data || [];
  const total = data?.pagination?.totalCount || 0;

  // Open modal to add a new product
  const openAddProductModal = () => {
    setSelectedProduct(null);
    setProductFormModalOpen(true);
  };

  // Open modal to edit a product
  const openEditProductModal = (product: Product) => {
    setSelectedProduct(product);
    setProductFormModalOpen(true);
  };

  // Open modal to delete a product
  const openDeleteProductModal = (product: Product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // Handle table changes (pagination, sorting)
  const handleTableChange = (pagination: any, _filters: unknown, sorter: any) => {
    const { current, pageSize } = pagination;

    if (pageSize !== limit) {
      setLimit(pageSize);
      setPage(1);
      return;
    }

    if (current !== page) {
      setPage(current);
      return;
    }

    if (sorter.columnKey !== sortField || sorter.order !== sortOrder) {
      setSortField(sorter.columnKey as string);
      setSortOrder(sorter.order as string);
      setPage(1);
    }
  };

  // Helper function to get product image URL
  const getProductImage = (product: Product) => {
    if (product.imageUrl) {
      // Check if imageUrl is already a full URL (starts with http)
      if (product.imageUrl.startsWith('http')) {
        return product.imageUrl;
      }
      // Otherwise, construct the S3 URL using environment variables
      const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'modlink-production';
      const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-east-2';
      return `https://${bucketName}.s3.${region}.amazonaws.com/${product.imageUrl}`;
    }
    return 'https://placehold.co/100x100?text=No+Image';
  };

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  // Calculate total stock from variants
  const getTotalStock = (product: Product): number => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce((total, variant) => total + (variant.stockQuantity || 0), 0);
    }
    return 0;
  };

  // Get base price (lowest price from variants)
  const getBasePrice = (product: Product): number => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => Number(v.price) || 0);
      return Math.min(...prices);
    }
    return 0;
  };

  // Table columns configuration
  const columns: TableColumnsType<Product> = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (_, record) => (
        <img 
          src={getProductImage(record)} 
          alt={record.name}
          className="w-12 h-12 object-cover"
          onError={(e) => {
            e.currentTarget.src = 'https://placehold.co/100x100?text=No+Image';
          }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text: string) => (
        <span className="font-orator text-white font-semibold">{text}</span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <span className="font-orator font-bold text-white">
          {text.length > 100 ? `${text.substring(0, 100)}...` : text}
        </span>
      ),
    },
    {
      title: 'Base Price',
      dataIndex: 'price',
      key: 'price',
      sorter: true,
      render: (_: number, record: Product) => {
        const basePrice = getBasePrice(record);
        const variantCount = record.variants?.length || 0;
        return (
          <div className="flex flex-col">
            <span className="font-orator font-bold text-orangicide">
              {formatPrice(basePrice)}
            </span>
            {variantCount > 1 && (
              <span className="font-orator text-zinc-400 text-xs">
                {variantCount} variants
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total Stock',
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      sorter: true,
      width: 120,
      render: (_: number, record: Product) => {
        const totalStock = getTotalStock(record);
        let colorClass = 'text-green-500'; // More than 10
        if (totalStock === 0) {
          colorClass = 'text-red-500';
        } else if (totalStock <= 10) {
          colorClass = 'text-orangicide';
        }
        
        return (
          <div className="flex flex-col items-center">
            <span className={`font-orator ${colorClass} font-bold text-md`}>
              {totalStock}
            </span>
            {totalStock === 0 && (
              <span className="font-orator text-red-500 text-xs">
                OUT OF STOCK
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date: string) => (
        <span className="font-orator font-bold text-white">
          {dayjs(date).format('MMM DD, YYYY')}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div className="flex gap-2">
          <Tooltip title="View details">
            <Button
              icon={<Eye size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/products/${record.id}`);
              }}
              type="text"
              className="!text-white [&:hover_svg]:!drop-shadow-[0_0_2px_#22C55E] !bg-transparent hover:!bg-transparent"
            />
          </Tooltip>
          <Tooltip title="Edit product">
            <Button
              icon={<PencilSimple size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                openEditProductModal(record);
              }}
              type="text"
              className="!text-white [&:hover_svg]:!drop-shadow-[0_0_2px_#3B82F6] !bg-transparent hover:!bg-transparent"
            />
          </Tooltip>
          <Tooltip title="Delete product">
            <Button
              icon={<Trash size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                openDeleteProductModal(record);
              }}
              type="text"
              danger
              className="!text-white [&:hover_svg]:!drop-shadow-[0_0_2px_#F74902] !bg-transparent hover:!bg-transparent"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="overflow-x-hidden">
      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat w-full py-12">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-6">
            <div className="font-displayed !font-normal md:!text-lg lg:!text-5xl text-orangicide">
              PRODUCTS MANAGEMENT
            </div>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={openAddProductModal}
              className="btn-orangicide text-black font-orator !text-xl w-45 !h-12"
            >
              ADD PRODUCT
            </Button>
          </div>

          <Paragraph className="!text-white font-orator !text-xl tracking-wider">
            Manage your product catalog - add, edit, or remove products
          </Paragraph>

          <WrapperPage isLoading={isLoading}>
            <GenericList<Product>
              columns={columns}
              dataSource={products}
              onRow={(record) => ({
                onClick: () => openEditProductModal(record),
              })}
              onChange={handleTableChange}
              onSearch={handleSearch}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange(page, pageSize) {
                  setPage(page);
                  setLimit(pageSize || 10);
                },
                showSizeChanger: true,
              }}
            />
          </WrapperPage>

          {/* Modal for adding/editing product */}
          <ProductFormModal
            open={productFormModalOpen}
            onCancel={() => setProductFormModalOpen(false)}
            editingProduct={selectedProduct}
            onSuccess={() => {
              setProductFormModalOpen(false);
              refetch();
            }}
          />

          {/* Modal to confirm deletion */}
          <DeleteProductModal
            open={deleteModalOpen}
            onCancel={() => setDeleteModalOpen(false)}
            product={selectedProduct}
            onSuccess={() => {
              setDeleteModalOpen(false);
              refetch();
            }}
          />
        </div>
      </section>
    </div>
  );
};
